import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, CalendarDays, CheckCircle2, Clipboard, ClipboardCheck, GraduationCap, KeyRound, ShieldCheck, Trophy, UserPlus, X } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { PAGE_SEO, SCHOLARSHIP_SCHEMA } from '../../config/seoConfig';
import { useAuth } from '../../contexts/AuthContext';
import scholarshipService from '../../services/scholarship.service';
import {
  ScholarshipCampaign,
  ScholarshipRegistration,
  ScholarshipRegistrationPayload,
} from '../../types/scholarship';

const SCHOLARSHIP_CLASS_LEVEL = 'Class 10';
const DEFAULT_SCHOLARSHIP_TITLE = 'N. Surya Narayana Memorial Scholarship 2026';
const DEFAULT_SCHOLARSHIP_DESCRIPTION =
  'The N. Surya Narayana Memorial Scholarship has been established in loving memory of Late Sri N. Surya Narayana Garu, a passionate teacher who dedicated his life to nurturing knowledge, values, and education among students.';
const DEFAULT_SCHOLARSHIP_PURPOSE =
  'This scholarship aims to recognize talented and deserving students, encourage academic excellence, and continue his legacy of supporting education.';
const DEFAULT_TELUGU_DESCRIPTION = `విద్యార్థుల విద్యాభివృద్ధి కోసం అంకితభావంతో కృషి చేసిన
శ్రీ ఎన్. సూర్యనారాయణ గారి స్మారకార్థం ఈ స్కాలర్‌షిప్ కార్యక్రమాన్ని నిర్వహిస్తున్నాము.

ప్రతిభావంతులైన విద్యార్థులను ప్రోత్సహించడం, వారికి విద్యా అవకాశాలను అందించడం మరియు ఆయన విద్యాసేవా వారసత్వాన్ని కొనసాగించడం ఈ కార్యక్రమం ముఖ్య ఉద్దేశ్యం.`;
const DEFAULT_PARTICIPATION_CERTIFICATE =
  'This certificate is proudly presented to [Student Name] for participating in the N. Surya Narayana Memorial Scholarship Examination 2026 conducted by LocalAlly in honor of a dedicated teacher whose passion for education continues to inspire future generations.';
const DEFAULT_MERIT_CERTIFICATE =
  'This certificate is awarded to [Student Name] for securing a distinguished position in the N. Surya Narayana Memorial Scholarship Examination 2026.\n\nPresented in memory of Late Sri N. Surya Narayana Garu, a passionate teacher whose dedication to education continues to inspire young minds.';
const DEFAULT_VISION =
  'To encourage academic excellence among students while honoring the educational values and teaching legacy of Late Sri N. Surya Narayana Garu.';

const emptyForm: ScholarshipRegistrationPayload = {
  scholarshipId: '',
  studentFirstName: '',
  studentLastName: '',
  studentName: '',
  studentEmail: '',
  studentPhone: '',
  parentName: '',
  parentPhone: '',
  relationToStudent: '',
  classLevel: SCHOLARSHIP_CLASS_LEVEL,
  schoolName: '',
  city: '',
  state: '',
  registeredByRole: 'STUDENT',
};

const formatDate = (value?: string) => {
  if (!value) return 'To be announced';
  return new Date(value).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DEFAULT_HERO_FEATURES = [
  'Free Registration',
  'Scholarship Examination',
  'Merit-Based Selection',
  'Interview Round',
  'Scholarship Awards',
];

const DEFAULT_RECOGNITION = [
  'Top 10 Students: Cash Prize + Merit Certificate',
  'Top 20 Students: Merit Certificate',
  'All Participants: Digital Participation Certificate',
];

const DEFAULT_PRIZE_STRUCTURE = [
  { rank: 1, title: '1st Prize', amount: 3000, currency: 'INR' },
  { rank: 2, title: '2nd Prize', amount: 2000, currency: 'INR' },
  { rank: 3, title: '3rd Prize', amount: 1000, currency: 'INR' },
  { rank: 4, title: '4th Rank', amount: 500, currency: 'INR' },
  { rank: 5, title: '5th Rank', amount: 500, currency: 'INR' },
  { rank: 6, title: '6th Rank', amount: 500, currency: 'INR' },
  { rank: 7, title: '7th Rank', amount: 500, currency: 'INR' },
  { rank: 8, title: '8th Rank', amount: 500, currency: 'INR' },
  { rank: 9, title: '9th Rank', amount: 500, currency: 'INR' },
  { rank: 10, title: '10th Rank', amount: 500, currency: 'INR' },
];

const formatCurrency = (amount: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message[0]) return String(message[0]);
  return fallback;
};

const RequiredMark = () => <span className="ml-1 text-rose-600" aria-hidden="true">*</span>;

const requiredRegistrationFields: Array<{ field: keyof ScholarshipRegistrationPayload; label: string }> = [
  { field: 'studentFirstName', label: 'First name' },
  { field: 'studentLastName', label: 'Last name(s)' },
  { field: 'classLevel', label: 'Class' },
  { field: 'studentPhone', label: 'Phone' },
  { field: 'schoolName', label: 'School' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
];

const trimValue = (value?: string) => (value || '').trim();

const getMissingRegistrationFields = (payload: ScholarshipRegistrationPayload) =>
  requiredRegistrationFields
    .filter(({ field }) => !trimValue(payload[field] as string | undefined))
    .map(({ label }) => label);

const buildExamAccessUrl = (
  scholarshipId: string,
  registrationId?: string,
  studentPhone?: string,
  autoAccess = false,
) => {
  const params = new URLSearchParams({ scholarshipId });
  if (registrationId) params.set('registrationId', registrationId);
  if (studentPhone) params.set('phone', studentPhone);
  if (autoAccess && registrationId && studentPhone) params.set('autoAccess', '1');
  return `/scholarship/exam?${params.toString()}`;
};

const getTimestamp = (value?: string) => {
  if (!value) return undefined;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

const getRegistrationClosedMessage = (campaign: ScholarshipCampaign) =>
  `${campaign.title} registration is closed. Registrations were open from ${formatDate(campaign.applicationStartDate)} to ${formatDate(campaign.applicationEndDate)}. If you already registered, use the exam access section with your hall ticket number and registered phone number.`;

const getRegistrationUnavailableMessage = (campaign: ScholarshipCampaign) =>
  `${campaign.title} registration is not open right now. Please use the exam access section if you already have a hall ticket number.`;

const getRegistrationWindowState = (campaign: ScholarshipCampaign) => {
  const applicationStart = getTimestamp(campaign.applicationStartDate);
  const applicationEnd = getTimestamp(campaign.applicationEndDate);
  const now = Date.now();

  if (campaign.status === 'DRAFT') {
    return {
      isOpen: false,
      tone: 'warning' as const,
      title: 'Registration not open',
      message: `${campaign.title} registrations are not open yet.`,
    };
  }

  if (campaign.status === 'CLOSED') {
    return {
      isOpen: false,
      tone: 'closed' as const,
      title: 'Registration closed',
      message: getRegistrationClosedMessage(campaign),
    };
  }

  if (applicationStart && applicationStart > now) {
    return {
      isOpen: false,
      tone: 'warning' as const,
      title: 'Registration opens soon',
      message: `${campaign.title} registration opens on ${formatDate(campaign.applicationStartDate)}. Please come back once registrations open.`,
    };
  }

  if (applicationEnd && applicationEnd < now) {
    return {
      isOpen: false,
      tone: 'closed' as const,
      title: 'Registration closed',
      message: getRegistrationClosedMessage(campaign),
    };
  }

  return {
    isOpen: true,
    tone: 'open' as const,
    title: 'Registration open',
    message: `Registration is open until ${formatDate(campaign.applicationEndDate)}.`,
  };
};

const getRegistrationNoticeClass = (tone: 'open' | 'warning' | 'closed') => {
  if (tone === 'closed') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (tone === 'warning') return 'border-amber-200 bg-amber-50 text-amber-900';
  return 'border-cyan-200 bg-cyan-50 text-cyan-900';
};

const getRegistrationSubmitError = (error: any, campaign: ScholarshipCampaign) => {
  const message = getErrorMessage(error, 'Registration could not be completed.');
  if (/registration.*closed/i.test(message)) return getRegistrationClosedMessage(campaign);
  if (/registration.*not open|not opened|has not opened/i.test(message)) {
    return getRegistrationUnavailableMessage(campaign);
  }
  return message;
};

const isMemorialCampaign = (campaign?: ScholarshipCampaign | null) => {
  if (!campaign) return false;
  return [campaign.scholarshipId, campaign.slug, campaign.title]
    .filter(Boolean)
    .some((value) => /n[-.\s]*surya|surya narayana|localally memorial/i.test(String(value)));
};

const normalizeMemorialCampaignContent = (campaign: ScholarshipCampaign): ScholarshipCampaign => {
  if (!isMemorialCampaign(campaign)) return campaign;
  return {
    ...campaign,
    title: campaign.title ?? DEFAULT_SCHOLARSHIP_TITLE,
    description: campaign.description ?? DEFAULT_SCHOLARSHIP_DESCRIPTION,
    tagline: campaign.tagline ?? 'Honoring a Passionate Teacher. Empowering Future Learners.',
    heroSubtitle: campaign.heroSubtitle ?? 'A Tribute to a Passionate Teacher. A Step Towards a Brighter Future.',
    heroFeatures: campaign.heroFeatures ?? DEFAULT_HERO_FEATURES,
    teluguDescription: campaign.teluguDescription ?? DEFAULT_TELUGU_DESCRIPTION,
    memorialName: campaign.memorialName ?? 'Late Sri N. Surya Narayana Garu',
    sponsorName: campaign.sponsorName ?? 'LocalAlly',
    purpose: campaign.purpose ?? DEFAULT_SCHOLARSHIP_PURPOSE,
    eligibility: campaign.eligibility ?? 'Class 10 students only.',
    recognitionHighlights: campaign.recognitionHighlights ?? DEFAULT_RECOGNITION,
    participationCertificateText: campaign.participationCertificateText ?? DEFAULT_PARTICIPATION_CERTIFICATE,
    meritCertificateText: campaign.meritCertificateText ?? DEFAULT_MERIT_CERTIFICATE,
    vision: campaign.vision ?? DEFAULT_VISION,
    ctaLabel: campaign.ctaLabel ?? 'Register Now',
    poweredByTagline: campaign.poweredByTagline ?? 'Connecting Students, Tutors and Opportunities.',
    prizeStructure: campaign.prizeStructure ?? DEFAULT_PRIZE_STRUCTURE,
    applicationStartDate: campaign.applicationStartDate ?? '2026-06-22T00:00:00.000+05:30',
    applicationEndDate: campaign.applicationEndDate ?? '2026-07-25T23:59:59.999+05:30',
    examStartDate: campaign.examStartDate ?? '2026-07-26T10:00:00.000+05:30',
    examEndDate: campaign.examEndDate ?? '2026-07-26T23:59:59.999+05:30',
    duration: campaign.duration ?? 30,
    totalMarks: campaign.totalMarks ?? 30,
    passingPercentage: campaign.passingPercentage ?? 80,
    totalQuestions: campaign.totalQuestions ?? 30,
  };
};

const ScholarshipPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [campaigns, setCampaigns] = useState<ScholarshipCampaign[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState<ScholarshipRegistrationPayload>(emptyForm);
  const [examAccess, setExamAccess] = useState({ registrationId: '', studentPhone: '' });
  const [registration, setRegistration] = useState<ScholarshipRegistration | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accessingExam, setAccessingExam] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [examAccessError, setExamAccessError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;
    scholarshipService
      .listScholarships()
      .then((data) => {
        if (!mounted) return;
        const nextCampaigns = Array.isArray(data) ? data : [];
        setCampaigns(nextCampaigns);
        const first = nextCampaigns.find(isMemorialCampaign) || nextCampaigns[0];
        if (first) {
          setSelectedId(first.scholarshipId);
          setForm((prev) => ({ ...prev, scholarshipId: first.scholarshipId }));
        }
      })
      .catch((err) => {
        console.error('Failed to load scholarships', err);
        if (mounted) setError('Unable to load scholarship details right now.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      scholarshipId: selectedId,
      classLevel: SCHOLARSHIP_CLASS_LEVEL,
      studentEmail: prev.studentEmail || currentUser?.email || '',
      studentPhone: prev.studentPhone || currentUser?.phone || '',
      registeredByRole: currentUser?.role === 'STUDENT' ? 'STUDENT' : prev.registeredByRole,
    }));
  }, [currentUser?.email, currentUser?.phone, currentUser?.role, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    setExamAccess({
      registrationId: window.localStorage.getItem(`localally-scholarship-registration-${selectedId}`) || '',
      studentPhone: window.localStorage.getItem(`localally-scholarship-phone-${selectedId}`) || currentUser?.phone || '',
    });
  }, [currentUser?.phone, selectedId]);

  const selectedCampaign = useMemo(
    () => {
      const campaign = campaigns.find((item) => item.scholarshipId === selectedId) || campaigns.find(isMemorialCampaign) || campaigns[0];
      return campaign ? normalizeMemorialCampaignContent(campaign) : undefined;
    },
    [campaigns, selectedId],
  );
  const scholarshipSeo = (
    <SEO
      title={PAGE_SEO.SCHOLARSHIP.title}
      description={PAGE_SEO.SCHOLARSHIP.description}
      path={PAGE_SEO.SCHOLARSHIP.path}
      schema={SCHOLARSHIP_SCHEMA}
    />
  );

  const handleChange = (field: keyof ScholarshipRegistrationPayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitRegistration = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCampaign) return;

    const windowState = getRegistrationWindowState(selectedCampaign);
    if (!windowState.isOpen) {
      setError(windowState.message);
      return;
    }

    const trimmedForm: ScholarshipRegistrationPayload = {
      ...form,
      scholarshipId: selectedCampaign.scholarshipId,
      studentFirstName: trimValue(form.studentFirstName),
      studentLastName: trimValue(form.studentLastName),
      studentEmail: trimValue(form.studentEmail),
      studentPhone: trimValue(form.studentPhone),
      parentName: trimValue(form.parentName),
      parentPhone: trimValue(form.parentPhone),
      relationToStudent: trimValue(form.relationToStudent),
      classLevel: SCHOLARSHIP_CLASS_LEVEL,
      schoolName: trimValue(form.schoolName),
      city: trimValue(form.city),
      state: trimValue(form.state),
    };
    const missingFields = getMissingRegistrationFields(trimmedForm);
    if (missingFields.length) {
      setError(`Please complete the required fields: ${missingFields.join(', ')}.`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const saved = await scholarshipService.register({
        ...trimmedForm,
        studentName: `${trimmedForm.studentFirstName} ${trimmedForm.studentLastName}`.trim(),
        classLevel: SCHOLARSHIP_CLASS_LEVEL,
      });
      setRegistration(saved);
      setShowSuccess(true);
      setExamAccess({
        registrationId: saved.registrationId,
        studentPhone: trimmedForm.studentPhone || '',
      });
      window.localStorage.setItem(
        `localally-scholarship-registration-${selectedCampaign.scholarshipId}`,
        saved.registrationId,
      );
      window.localStorage.setItem(
        `localally-scholarship-phone-${selectedCampaign.scholarshipId}`,
        trimmedForm.studentPhone || '',
      );
    } catch (err: any) {
      setError(getRegistrationSubmitError(err, selectedCampaign));
    } finally {
      setSubmitting(false);
    }
  };

  const openExamDashboard = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCampaign) return;

    const registrationId = examAccess.registrationId.trim();
    const studentPhone = examAccess.studentPhone.trim();
    if (!registrationId || !studentPhone) {
      setExamAccessError('Enter the registered phone number and hall ticket number.');
      return;
    }

    try {
      setAccessingExam(true);
      setExamAccessError(null);
      const response = await scholarshipService.accessExam({
        scholarshipId: selectedCampaign.scholarshipId,
        registrationId,
        studentPhone,
      });
      window.localStorage.setItem(
        `localally-scholarship-registration-${response.campaign.scholarshipId}`,
        response.registration.registrationId,
      );
      window.localStorage.setItem(
        `localally-scholarship-phone-${response.campaign.scholarshipId}`,
        studentPhone,
      );
      navigate(buildExamAccessUrl(
        response.campaign.scholarshipId,
        response.registration.registrationId,
        studentPhone,
        true,
      ));
    } catch (err: any) {
      setExamAccessError(getErrorMessage(err, 'Unable to verify scholarship exam access.'));
    } finally {
      setAccessingExam(false);
    }
  };

  if (loading) {
    return (
      <>
        {scholarshipSeo}
        <div className="min-h-screen px-4 py-16 text-center text-slate-600">
          Loading scholarship program...
        </div>
      </>
    );
  }

  if (!selectedCampaign) {
    return (
      <>
        {scholarshipSeo}
        <div className="min-h-screen px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold text-slate-950">Scholarships are coming soon.</h1>
          <p className="mt-2 text-sm text-slate-600">No active campaign is published yet.</p>
        </div>
      </>
    );
  }

  const registeredPhone = (form.studentPhone || '').trim();
  const examLink = registration
    ? buildExamAccessUrl(selectedCampaign.scholarshipId, registration.registrationId, registeredPhone, true)
    : buildExamAccessUrl(selectedCampaign.scholarshipId);
  const heroFeatures = selectedCampaign.heroFeatures ?? DEFAULT_HERO_FEATURES;
  const recognitionHighlights = selectedCampaign.recognitionHighlights ?? DEFAULT_RECOGNITION;
  const totalPrizePool = selectedCampaign.prizeStructure.reduce(
    (sum, prize) => sum + Number(prize.amount || 0),
    0,
  );
  const certificateCards = [
    {
      title: 'Certificate of Participation',
      text: selectedCampaign.participationCertificateText || DEFAULT_PARTICIPATION_CERTIFICATE,
    },
    {
      title: 'Certificate of Merit',
      text: selectedCampaign.meritCertificateText || DEFAULT_MERIT_CERTIFICATE,
    },
  ];
  const rawCtaLabel = (selectedCampaign.ctaLabel || '').trim();
  const applyLabel = rawCtaLabel.toLowerCase() === 'apply now' ? 'Register Now' : rawCtaLabel || 'Register Now';
  const registrationWindow = getRegistrationWindowState(selectedCampaign);
  const registrationNoticeClass = getRegistrationNoticeClass(registrationWindow.tone);
  const registrationDeadlineText = `Registrations are open until ${formatDate(selectedCampaign.applicationEndDate)}.`;
  const examScheduleText = `The scholarship exam is scheduled for ${formatDate(selectedCampaign.examStartDate)}.`;
  const campaignNotice = `Important update: ${registrationDeadlineText} ${examScheduleText}`;

  return (
    <>
      {scholarshipSeo}
      <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_48%,_#fff7ed_100%)] text-slate-950">
        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div
          className="mb-6 overflow-hidden rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-sm"
          aria-label={campaignNotice}
        >
          <div className="scholarship-marquee whitespace-nowrap">
            <span>{campaignNotice}</span>
            <span className="ml-12" aria-hidden="true">{campaignNotice}</span>
          </div>
        </div>
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_420px] lg:items-start">
          <div className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">
                  <Trophy className="h-4 w-4" />
                  Scholarship
                </span>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  {selectedCampaign.status.replace('_', ' ')}
                </span>
              </div>
              <h1 className="mt-5 max-w-4xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                {selectedCampaign.title}
              </h1>
              {selectedCampaign.tagline && (
                <p className="mt-3 max-w-3xl text-sm font-semibold uppercase tracking-[0.16em] text-cyan-700">
                  {selectedCampaign.tagline}
                </p>
              )}
              <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
                {selectedCampaign.heroSubtitle || selectedCampaign.description}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CalendarDays className="h-5 w-5 text-cyan-700" />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Registration</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">
                    {formatDate(selectedCampaign.applicationStartDate)} to {formatDate(selectedCampaign.applicationEndDate)}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <ClipboardCheck className="h-5 w-5 text-emerald-700" />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Format</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">
                    {selectedCampaign.totalQuestions} questions, {selectedCampaign.totalMarks} marks, {selectedCampaign.duration} minutes
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <ShieldCheck className="h-5 w-5 text-amber-700" />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Pass mark</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">
                    {selectedCampaign.passingPercentage}% minimum
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <GraduationCap className="h-5 w-5 text-rose-700" />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Exam opens</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">
                    {formatDate(selectedCampaign.examStartDate)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-950">About The Scholarship</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">{selectedCampaign.description}</p>
              {selectedCampaign.purpose && (
                <p className="mt-3 text-sm leading-7 text-slate-600">{selectedCampaign.purpose}</p>
              )}
              {selectedCampaign.teluguDescription && (
                <p lang="te" className="mt-5 whitespace-pre-line rounded-lg border border-cyan-100 bg-cyan-50 p-4 text-sm leading-7 text-slate-800">
                  {selectedCampaign.teluguDescription}
                </p>
              )}
              {selectedCampaign.vision && (
                <div className="mt-5 rounded-lg border border-amber-100 bg-amber-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">Vision</p>
                  <p className="mt-2 text-sm leading-6 text-amber-950">{selectedCampaign.vision}</p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">Scholarship Prize Structure</h2>
                  <p className="mt-1 text-sm text-slate-500">Total Prize Pool</p>
                </div>
                <p className="text-3xl font-semibold tracking-tight text-slate-950">
                  {formatCurrency(totalPrizePool)}
                </p>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {selectedCampaign.prizeStructure.map((prize) => (
                  <div key={prize.rank} className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <Award className="h-6 w-6 text-amber-600" />
                    <p className="mt-4 text-sm font-semibold text-slate-500">{prize.title}</p>
                    <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                      {formatCurrency(prize.amount, prize.currency)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-950">Program Highlights</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {heroFeatures.map((item) => (
                  <div key={item} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                    <CheckCircle2 className="h-5 w-5 text-cyan-700" />
                    <p className="mt-2 text-sm font-semibold text-slate-900">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-950">Exam And Next Steps</h2>
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-cyan-100 bg-cyan-50 p-4 text-sm leading-6 text-cyan-950">
                  {registrationDeadlineText}
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                  {examScheduleText}
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-emerald-950">
                  Remaining process details will be intimated only to shortlisted students.
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-950">Certificates</h2>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {certificateCards.map((item) => (
                  <div key={item.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-950">{item.title}</p>
                    <p className="mt-3 whitespace-pre-line text-sm leading-7 text-slate-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-950">Additional Recognition</h2>
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                {recognitionHighlights.map((item) => (
                  <div key={item} className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-900">
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-950 p-4 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">Powered By LocalAlly</p>
                <p className="mt-2 text-sm text-slate-200">
                  {selectedCampaign.poweredByTagline || 'Connecting Students, Tutors and Opportunities.'}
                </p>
              </div>
            </div>
          </div>

          <aside className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-slate-950 p-2 text-white">
                <UserPlus className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Register Student</h2>
                <p className="text-sm text-slate-500">Free registration. One entry per student.</p>
              </div>
            </div>

            <div className="mt-5 border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
              <p className="font-semibold text-amber-950">Important note</p>
              <p className="mt-1">
                {selectedCampaign.eligibility || 'This scholarship program is only for Class 10 students.'} Enter the student's first name and last name(s)
                exactly as printed on the Aadhaar card. Shortlisted students must carry their Aadhaar card for
                verification at the interview.
              </p>
              <p className="mt-2">
                {selectedCampaign.instructions || `${registrationDeadlineText} ${examScheduleText}`}
              </p>
              <p className="mt-2">
                This campaign is Class 10-specific, but LocalAlly as a platform supports tutor and coaching discovery
                for all classes, subjects, boards and competitive exams.
              </p>
            </div>

            {campaigns.length > 1 && (
              <label className="mt-5 block text-sm font-medium text-slate-700">
                Campaign
                <select
                  value={selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                >
                  {campaigns.map((campaign) => (
                    <option key={campaign.scholarshipId} value={campaign.scholarshipId}>
                      {normalizeMemorialCampaignContent(campaign).title}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!registrationWindow.isOpen ? (
              <div className={`mt-5 rounded-lg border px-4 py-3 text-sm leading-6 ${registrationNoticeClass}`}>
                <p className="font-semibold">{registrationWindow.title}</p>
                <p className="mt-1">{registrationWindow.message}</p>
                <Link
                  to={examLink}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Access scholarship exam
                </Link>
              </div>
            ) : (
            <form className="mt-5 space-y-4" onSubmit={submitRegistration}>
              <div className={`rounded-lg border px-4 py-3 text-sm leading-6 ${registrationNoticeClass}`}>
                <p className="font-semibold">{registrationWindow.title}</p>
                <p className="mt-1">{registrationWindow.message}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  First name<RequiredMark />
                  <input
                    value={form.studentFirstName || ''}
                    onChange={(event) => handleChange('studentFirstName', event.target.value)}
                    required
                    autoComplete="given-name"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Last name(s)<RequiredMark />
                  <input
                    value={form.studentLastName || ''}
                    onChange={(event) => handleChange('studentLastName', event.target.value)}
                    required
                    autoComplete="family-name"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Class<RequiredMark />
                  <input
                    value={SCHOLARSHIP_CLASS_LEVEL}
                    required
                    readOnly
                    aria-readonly="true"
                    className="mt-2 w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Phone<RequiredMark />
                  <input
                    value={form.studentPhone}
                    onChange={(event) => handleChange('studentPhone', event.target.value)}
                    required
                    inputMode="tel"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <label className="block text-sm font-medium text-slate-700">
                Email
                <input
                  type="email"
                  value={form.studentEmail}
                  onChange={(event) => handleChange('studentEmail', event.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                School<RequiredMark />
                <input
                  value={form.schoolName}
                  onChange={(event) => handleChange('schoolName', event.target.value)}
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  City<RequiredMark />
                  <input
                    value={form.city}
                    onChange={(event) => handleChange('city', event.target.value)}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  State<RequiredMark />
                  <input
                    value={form.state}
                    onChange={(event) => handleChange('state', event.target.value)}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Parent name
                  <input
                    value={form.parentName}
                    onChange={(event) => handleChange('parentName', event.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Parent phone
                  <input
                    value={form.parentPhone}
                    onChange={(event) => handleChange('parentPhone', event.target.value)}
                    inputMode="tel"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
              </div>
              <label className="block text-sm font-medium text-slate-700">
                Relation to student
                <select
                  value={form.relationToStudent}
                  onChange={(event) => handleChange('relationToStudent', event.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                >
                  <option value="">Select relation</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Other">Other</option>
                </select>
              </label>

              {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

              {registration && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  Registration confirmed: <span className="font-semibold">{registration.registrationId}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UserPlus className="h-4 w-4" />
                {submitting ? 'Registering...' : applyLabel}
              </button>

              <Link
                to={examLink}
                className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Access scholarship exam
              </Link>
            </form>
            )}

            <div className="mt-6 border-t border-slate-200 pt-5">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-cyan-50 p-2 text-cyan-700">
                  <KeyRound className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-950">Sign in for exam</h3>
                  <p className="text-xs leading-5 text-slate-500">
                    Use the registered phone number and hall ticket number. This is separate from LocalAlly app login.
                  </p>
                </div>
              </div>
              <form className="mt-4 space-y-3" onSubmit={openExamDashboard}>
                <label className="block text-sm font-medium text-slate-700">
                  Registered phone number
                  <input
                    value={examAccess.studentPhone}
                    onChange={(event) => setExamAccess((prev) => ({ ...prev, studentPhone: event.target.value }))}
                    required
                    inputMode="tel"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Hall ticket number
                  <input
                    value={examAccess.registrationId}
                    onChange={(event) => setExamAccess((prev) => ({ ...prev, registrationId: event.target.value }))}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                  />
                </label>
                {examAccessError && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {examAccessError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={accessingExam}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-semibold text-cyan-800 transition hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {accessingExam ? 'Verifying...' : 'Open exam dashboard'}
                </button>
              </form>
            </div>
          </aside>
        </div>
        </section>

        {registration && showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Registration successful</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-950">Keep this hall ticket number safe</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
                aria-label="Close registration success message"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-700">Hall ticket number</p>
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="break-all font-mono text-lg font-semibold text-emerald-950">{registration.registrationId}</p>
                <button
                  type="button"
                  onClick={() => void navigator.clipboard?.writeText(registration.registrationId)}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                >
                  <Clipboard className="h-4 w-4" />
                  Copy
                </button>
              </div>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600">
              Use this hall ticket number with the registered phone number to access the scholarship exam. {examScheduleText} Further process will be intimated only to shortlisted students.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to={examLink}
                className="inline-flex flex-1 items-center justify-center rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700"
              >
                Open exam access
              </Link>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                className="inline-flex flex-1 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ScholarshipPage;
